import { CatalogSkillLevelPill } from "@/components/courses/CatalogSkillLevelPill";
import { COURSE_THUMBNAIL_ASPECT } from "@/components/ui/CourseThumbnailCard";
import { ListRowThumbnailColumn } from "@/components/ui/ListRowThumbnailColumn";
import { BRAND_COLORS } from "@/lib/brandColors";
import type { Course } from "@/types";
import { Link } from "expo-router";
import React from "react";
import { Platform } from "react-native";
import { XStack, YStack } from "tamagui";
import { BodyText, CardTitle, EyebrowText } from "@/components/ui/Typography";

type Props = {
  course: Course;
  href: string;
  thumbnailUri?: string | null;
  /** 0–100 */
  progressPercentage?: number;
  progressBarHeight?: number;
  isMobile: boolean;
  /** Runs before navigation (e.g. search-click analytics). */
  onPress?: () => void;
};

/** Catalog list row — horizontal thumbnail + title/meta, mirrors lesson list rows. */
export const CoursesCatalogRow = React.memo(function CoursesCatalogRow({
  course,
  href,
  thumbnailUri,
  progressPercentage = 0,
  progressBarHeight = 4,
  isMobile,
  onPress,
}: Props) {
  const showCompletedCheck = progressPercentage >= 100;
  const titleLineCount = isMobile ? 3 : 2;
  const instructorNames =
    course.instructor_names?.filter(Boolean).join(" & ") ?? "";

  return (
    <Link href={href as any} asChild onPress={onPress}>
      <XStack
        ai="stretch"
        bg={BRAND_COLORS.cardBg}
        br={10}
        overflow="hidden"
        borderWidth={1}
        borderColor="rgba(0,0,0,0.08)"
        boxShadow="0 1px 6px rgba(0,0,0,0.08)"
        accessibilityLabel={course.title}
        style={{ cursor: Platform.OS === "web" ? "pointer" : undefined }}
        hoverStyle={{ borderColor: "rgba(0,0,0,0.12)" }}
        pressStyle={{ opacity: 0.96 }}
      >
        <ListRowThumbnailColumn
          thumbnailUri={thumbnailUri}
          aspectRatio={COURSE_THUMBNAIL_ASPECT}
          progress={progressPercentage}
          progressBarHeight={progressBarHeight}
          showCheckmark={showCompletedCheck}
          imageEngine="expo"
          recyclingKey={thumbnailUri ?? undefined}
        />

        <YStack flex={1} p={16} gap={6} jc="center" minWidth={0}>
          <XStack ai="flex-start" jc="space-between" gap={12}>
            <CardTitle
              flex={1}
              color={BRAND_COLORS.text}
              numberOfLines={titleLineCount}
              minWidth={0}
            >
              {course.title}
            </CardTitle>
            {course.skill_level != null ? (
              <CatalogSkillLevelPill level={course.skill_level} />
            ) : null}
          </XStack>
          {instructorNames ? (
            <EyebrowText color={BRAND_COLORS.instructorMeta} numberOfLines={1}>
              {instructorNames}
            </EyebrowText>
          ) : null}
          {!isMobile && !!course.summary && (
            <BodyText
              color={BRAND_COLORS.muted}
              numberOfLines={2}
              ellipsizeMode="tail"
              style={{ overflow: "hidden" }}
            >
              {course.summary}
            </BodyText>
          )}
        </YStack>
      </XStack>
    </Link>
  );
});
